// LeaderboardPanel — promoted from learner-app/components/gamification
// to @flowgenix/shared-ui (Phase 2 / Step C-3.3).
//
// Renderer for the `leaderboard` widget kind. Light deps:
// - react-i18next (provided by the workspace root; gracefully degrades
//   to key-as-text when no I18nextProvider is mounted)
// - WebSocket (browser-built-in)
//
// API client is injected via the `gamificationAPI` prop so the widget
// is decoupled from the learner-app's `services/api` singleton.
// Atlas Catalog can pass a mock API for previews.
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './LeaderboardPanel.css';

const RANK_MEDALS = ['🥇', '🥈', '🥉'];

/**
 * LeaderboardPanel
 *
 * Props:
 *   gamificationAPI  — required; an object with `.getLeaderboard({domain, scope, limit})`
 *                      returning a Promise<{data: {entries: Entry[]}}>
 *   domain           — domain_id string
 *   scope            — leaderboard scope: "global" | "program" | "team" | "fan_pool"
 *   limit            — max entries to show (default 20)
 *   sessionId        — if provided, opens a WebSocket to receive live leaderboard
 *                      updates emitted by FootballCompetitiveMatchMode
 *   currentLearnerId — highlights the current user's row
 *   competitive      — boolean; shows "LIVE" badge when true + scope picker
 *   wsBaseOverride   — optional override for the WebSocket base URL
 *                      (defaults to window.location-derived ws[s]://host)
 */
export default function LeaderboardPanel({
  gamificationAPI,
  domain,
  scope: initialScope,
  limit,
  sessionId,
  currentLearnerId,
  competitive,
  wsBaseOverride,
}) {
  const { t } = useTranslation('session');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState(initialScope || 'global');
  const [live, setLive] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    if (!gamificationAPI?.getLeaderboard) {
      setLoading(false);
      return;
    }
    setLoading(true);
    gamificationAPI
      .getLeaderboard({ domain, scope, limit: limit || 20 })
      .then(({ data }) => setEntries(data.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [domain, scope, limit, gamificationAPI]);

  useEffect(() => {
    if (!sessionId || !competitive) return;
    if (typeof window === 'undefined' || typeof WebSocket === 'undefined') return;

    const wsBase =
      wsBaseOverride
      || (window.location.protocol === 'https:' ? 'wss' : 'ws') + '://' + window.location.host;
    const ws = new WebSocket(`${wsBase}/api/scenario-os/ws/session/${sessionId}/leaderboard`);
    wsRef.current = ws;

    ws.onopen = () => setLive(true);
    ws.onclose = () => setLive(false);
    ws.onerror = () => setLive(false);

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'leaderboard_update' && msg.entries) {
          setEntries(msg.entries);
        }
      } catch (_) { /* malformed payload — ignore */ }
    };

    return () => {
      ws.close();
      setLive(false);
    };
  }, [sessionId, competitive, wsBaseOverride]);

  const SCOPES = ['global', 'program', 'team', 'fan_pool'];

  return (
    <div className="leaderboard-panel">
      <div className="leaderboard-header">
        <h3 className="leaderboard-title">
          {t('leaderboard', 'Leaderboard')}
          {live && <span className="lb-live-badge">● LIVE</span>}
        </h3>

        {competitive && (
          <div className="lb-scope-picker">
            {SCOPES.map((s) => (
              <button
                key={s}
                className={`lb-scope-btn ${scope === s ? 'active' : ''}`}
                onClick={() => setScope(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="leaderboard-loading">{t('loading_leaderboard', 'Loading leaderboard...')}</div>
      ) : (
        <div className="leaderboard-list">
          {entries.map((entry, i) => {
            const isMe = entry.learner_id === currentLearnerId;
            return (
              <div
                key={entry.learner_id || i}
                className={`leaderboard-row ${i < 3 ? 'top-three' : ''} ${isMe ? 'my-row' : ''}`}
              >
                <span className="lb-rank">
                  {i < 3 ? RANK_MEDALS[i] : `#${entry.rank || i + 1}`}
                </span>
                <span className="lb-name">
                  {entry.display_name || entry.learner_id}
                  {isMe && <span className="lb-you-badge"> (You)</span>}
                </span>
                <span className="lb-xp">{(entry.xp_total || 0).toLocaleString()} XP</span>
                <span className="lb-level">Lv {entry.level}</span>
              </div>
            );
          })}
          {entries.length === 0 && (
            <div className="leaderboard-empty">{t('leaderboard_empty', 'No entries yet. Be the first!')}</div>
          )}
        </div>
      )}
    </div>
  );
}

LeaderboardPanel.kind = 'leaderboard';
LeaderboardPanel.displayName = 'LeaderboardPanel';
